// ReplyDocument — renders a coach reply's markdown body with the pinned
// vocabulary from the designer handoff:
//   H3 (no H1/H2 — a reply is a short piece, not a report)
//   Paragraphs (max ~68ch)
//   `-` / `1.` lists (one level of nesting)
//   **bold** (lead-ins), *italic*
//   > blockquote — for "what you might say to the CFO" scripts (brand left-rule)
//   Tables (max 4 columns)
//   `---` → extra section space (NOT a drawn <hr>)
//   Text pills `[HIGH] / [MEDIUM] / [LOW]` → styled pills (NOT emoji)
//   Inline citations `[1]` / `[2]` — numbered, with hover preview tied to the
//     citations[] array.
//
// Anything outside this set renders as plain text, so the model can't invent
// styling we haven't designed.

import { type ComponentPropsWithoutRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { CoachCitation } from '@gc-coach/shared';
import styles from './Coach.module.css';

export interface ReplyDocumentProps {
  text: string;
  citations: readonly CoachCitation[];
  /** Called when the user clicks an inline [n] citation marker. The parent
   *  opens the ConsultedPanel + highlights that row (per designer Q4:
   *  "clicking a marker opens the panel and highlights its row"). */
  onCitationClick?: (n: number) => void;
}

/**
 * Transform the model's output into markdown our renderer understands:
 *   - Horizontal rules (`---` on their own line) → an empty paragraph with
 *     a sentinel class we style as extra space (not a drawn line).
 *   - Risk pills (`[HIGH]` / `[MEDIUM] / [LOW]`) → inline HTML <span> with a
 *     typed class the <p> renderer picks up and re-wraps. react-markdown
 *     doesn't eval HTML, so we substitute a special marker instead:
 *     `⟦risk:high⟧LOW⟧` is parsed by our <p> renderer below.
 *   - Inline citations (`[1]` / `[2]`) stay as plain markdown; a custom text
 *     renderer below swaps them for <CitationMarker/> when the number is
 *     within the citations[] bounds.
 */
function preprocess(raw: string): string {
  // --- → sentinel we hide and style as a gap.
  let out = raw.replace(/^---\s*$/gm, '⟦gap⟧');
  // Risk pills: [HIGH] / [MEDIUM] / [LOW] on word boundaries.
  out = out.replace(/\[(HIGH|MEDIUM|LOW)\]/g, (_m, level: string) => {
    const label = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' }[level] ?? level;
    const tone = level.toLowerCase();
    return `⟦risk:${tone}⟧${label}⟧`;
  });
  return out;
}

/**
 * Walk a string and emit either plain text or a <span> for each sentinel
 * (gap / risk / citation). Called from every renderer that gets text,
 * because react-markdown doesn't give you a text-node hook otherwise.
 */
function renderSentinels(
  text: string,
  citations: readonly CoachCitation[],
  onCitationClick?: (n: number) => void,
): Array<string | JSX.Element> {
  const out: Array<string | JSX.Element> = [];
  const pattern = /⟦gap⟧|⟦risk:(high|medium|low)⟧([^⟧]+)⟧|\[(\d+)\]/g;
  let cursor = 0;
  let key = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > cursor) out.push(text.slice(cursor, start));
    if (match[0] === '⟦gap⟧') {
      out.push(<span key={`g${key++}`} className={styles.docGap} aria-hidden="true" />);
    } else if (match[1]) {
      const tone = match[1];
      const label = match[2] ?? '';
      const toneClass =
        tone === 'high'
          ? styles.riskHigh
          : tone === 'medium'
          ? styles.riskMedium
          : styles.riskLow;
      out.push(
        <span key={`r${key++}`} className={`${styles.risk} ${toneClass}`}>
          {label}
        </span>,
      );
    } else if (match[3]) {
      const n = Number(match[3]);
      const citation = citations[n - 1];
      if (citation) {
        out.push(
          <CitationMarker
            key={`c${key++}`}
            n={n}
            citation={citation}
            {...(onCitationClick ? { onClick: onCitationClick } : {})}
          />,
        );
      } else {
        // Model referenced a citation we don't have — render the raw [n] so
        // the author of the system prompt can see the mismatch in staging.
        out.push(match[0]);
      }
    }
    cursor = start + match[0].length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

function CitationMarker({
  n,
  citation,
  onClick,
}: {
  n: number;
  citation: CoachCitation;
  onClick?: (n: number) => void;
}) {
  // Hover preview shrinks to type + title (per designer Q4). Click opens
  // the ConsultedPanel + highlights this row.
  const typeLabel = citation.type ? TYPE_LABEL_MAP[citation.type] : null;
  return (
    <button
      type="button"
      className={styles.cit}
      onClick={() => onClick?.(n)}
      aria-label={`Open source ${n}: ${citation.title}`}
    >
      {n}
      <span className={styles.citPreview} role="tooltip">
        {typeLabel && <b>{typeLabel}</b>}
        {citation.title}
      </span>
    </button>
  );
}

const TYPE_LABEL_MAP: Record<NonNullable<CoachCitation['type']>, string> = {
  qa_pair: 'GUIDANCE',
  framework: 'FRAMEWORK',
  case_study: 'CASE STUDY',
  document_excerpt: 'BOOK EXCERPT',
};

/** react-markdown passes children as React.ReactNode; walk and replace
 *  text nodes with sentinel-aware output. */
function withSentinels(
  citations: readonly CoachCitation[],
  onCitationClick?: (n: number) => void,
) {
  function RenderChildren({ children }: { children?: React.ReactNode }) {
    if (typeof children === 'string') {
      return <>{renderSentinels(children, citations, onCitationClick)}</>;
    }
    if (Array.isArray(children)) {
      return (
        <>
          {children.map((c, i) =>
            typeof c === 'string' ? (
              <RenderChildren key={i}>{c}</RenderChildren>
            ) : (
              c
            ),
          )}
        </>
      );
    }
    return <>{children}</>;
  }
  return RenderChildren;
}

export function ReplyDocument({ text, citations, onCitationClick }: ReplyDocumentProps) {
  const Children = withSentinels(citations, onCitationClick);
  return (
    <div className={styles.doc}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          // H3 is the only heading level the pinned vocab allows. Downgrade
          // h1/h2 (if the model ever emits them against instructions) to h3
          // so the layout stays coherent.
          h1: (p) => <h3 className={styles.docH3}><Children>{p.children}</Children></h3>,
          h2: (p) => <h3 className={styles.docH3}><Children>{p.children}</Children></h3>,
          h3: (p) => <h3 className={styles.docH3}><Children>{p.children}</Children></h3>,
          h4: (p) => <h3 className={styles.docH3}><Children>{p.children}</Children></h3>,
          h5: (p) => <h3 className={styles.docH3}><Children>{p.children}</Children></h3>,
          h6: (p) => <h3 className={styles.docH3}><Children>{p.children}</Children></h3>,
          p: (p) => <p className={styles.docP}><Children>{p.children}</Children></p>,
          ul: (p) => <ul className={styles.docList}>{p.children}</ul>,
          ol: (p) => <ol className={styles.docList}>{p.children}</ol>,
          li: (p) => <li className={styles.docLi}><Children>{p.children}</Children></li>,
          strong: (p) => <strong className={styles.docStrong}><Children>{p.children}</Children></strong>,
          em: (p) => <em><Children>{p.children}</Children></em>,
          blockquote: (p) => (
            <blockquote className={styles.docBlockquote}>{p.children}</blockquote>
          ),
          table: (p) => (
            <div className={styles.docTableScroll}>
              <table className={styles.docTable}>{p.children}</table>
            </div>
          ),
          thead: (p) => <thead>{p.children}</thead>,
          tbody: (p) => <tbody>{p.children}</tbody>,
          tr: (p) => <tr>{p.children}</tr>,
          th: (p) => <th className={styles.docTh}><Children>{p.children}</Children></th>,
          td: (p) => <td className={styles.docTd}><Children>{p.children}</Children></td>,
          // Drawn <hr> is explicitly rejected by the design — `---` already
          // got rewritten to ⟦gap⟧ in preprocess(). If one slips through
          // (e.g., --- with trailing spaces), still render as a gap.
          hr: () => <div className={styles.docGap} aria-hidden="true" />,
          // Links in the body were out of V1 scope per spec (links belong
          // in citations). Render as plain text so a stray link doesn't
          // introduce styling we don't own.
          a: (p: ComponentPropsWithoutRef<'a'>) => <span><Children>{p.children}</Children></span>,
          // Inline code: hidden from V1 vocab but render as plain text with
          // slight muting so a stray `foo` doesn't crash.
          code: (p) => <span className={styles.docInlineCode}><Children>{p.children}</Children></span>,
        }}
      >
        {preprocess(text)}
      </Markdown>
    </div>
  );
}
